import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CompaniesService,
  type Company,
  type CompanyCreate,
  type CompanyUpdate,
} from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useCompanies = () =>
  useQuery({
    queryKey: ["companies"],
    queryFn: () =>
      fetchAll<Company>((opts) =>
        CompaniesService.getApiCompanies({
          ...opts,
          query: { ...opts.query, order: "createdAt.asc" },
        }),
      ),
    staleTime: 5 * 60_000,
  });

export const useCompany = (id: string) =>
  useQuery({
    queryKey: ["company", id],
    queryFn: async (): Promise<Company> => {
      const res = await CompaniesService.getApiCompaniesById({
        path: { id },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Not found");
      return res.data as never;
    },
    enabled: !!id,
  });

function extractError(res: { error?: unknown }, fallback: string): string {
  const err = res.error;
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "error" in err && typeof (err as any).error === "string")
    return (err as any).error;
  return fallback;
}

export const useCreateCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CompanyCreate) => {
      const res = await CompaniesService.postApiCompanies({
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error(extractError(res, "Kunne ikke opprette bedrift"));
      return res.data as never as Company;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["companies"] }),
  });
};

export const useUpdateCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompanyUpdate }) => {
      const res = await CompaniesService.putApiCompaniesById({
        path: { id },
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error(extractError(res, "Kunne ikke oppdatere bedrift"));
      return res.data as never as Company;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["company"] });
    },
  });
};

export const useDeleteCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await CompaniesService.deleteApiCompaniesById({
        path: { id },
        headers: getAuthHeaders(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["companies"] }),
  });
};
